import React, { useMemo } from "react";

import { colors } from "@zapalacorp/tokens";
import { useTranslation } from "react-i18next";

import { IconAirFreight } from "../../../../../../../components/Generic/Icons/IconAirFreight";
import { IconNotGoing } from "../../../../../../../components/Generic/Icons/IconNotGoing";
import { IconResidence } from "../../../../../../../components/Generic/Icons/IconResidence";
import { IconRoadFreight } from "../../../../../../../components/Generic/Icons/IconRoadFreight";
import { IconSeaFreight } from "../../../../../../../components/Generic/Icons/IconSeaFreight";
import {
  LocationCustomerPortal,
  RoomJobCustomerPortal,
} from "../../../../../../../models/customer.portal";
import { Destination } from "../../../../../../../models/destination";
import { JobMode } from "../../../../../../../models/job.mode";
import { DESTINATION } from "../../../../../../../util/enums";
import {
  calculateByDestination,
  getAddressDestinationItems,
  getCustomDestinationItems,
  getCustomModeDestinationItems,
  getItemCounts,
} from "../../../../../../../util/getCustomerPortalDestinations";
import { getWeightMeasurement } from "../../../../../../../util/getMeasurement";
import {
  InfoCard,
  InfoContent,
  InfoHeader,
  InfoText,
  InfoTextContent,
  InfoTextValue,
  InfoTitle,
  SummaryHeader,
  SummarySection,
  SummaryText,
  SummaryTitle,
} from "./styles";

interface EstimatedInfoSectionProps {
  inventory: RoomJobCustomerPortal[];
  modes: JobMode[];
  destinationList?: Destination[];
  destinationAddresses?: LocationCustomerPortal[];
}

export const EstimatedInfoSection = ({
  inventory,
  modes,
  destinationList,
  destinationAddresses,
}: EstimatedInfoSectionProps) => {
  const { t: translate } = useTranslation();
  const measurementWeight = getWeightMeasurement();

  const addressDestinationItems = useMemo(() => {
    return getAddressDestinationItems(inventory, destinationAddresses);
  }, [inventory, destinationAddresses]);

  const customDestinationItems = useMemo(() => {
    return getCustomDestinationItems(inventory, destinationList);
  }, [inventory, destinationList]);

  const customModeDestinationItems = useMemo(() => {
    return getCustomModeDestinationItems(inventory, modes);
  }, [inventory, modes]);

  const getDestinationCardColors = (destination: string) => {
    let backgroundColor = colors.gray25;
    let borderColor = colors.gray200;
    let titleColor = colors.brandBlue500;

    switch (destination) {
      case DESTINATION.AIR:
        backgroundColor = colors.indigo25;
        borderColor = colors.indigo200;
        titleColor = colors.indigo600;
        break;
      case DESTINATION.SEA:
        backgroundColor = colors.blue25;
        borderColor = colors.blue200;
        titleColor = colors.blue600;
        break;
      case DESTINATION.ROAD:
        backgroundColor = colors.orange25;
        borderColor = colors.orange200;
        titleColor = colors.orange800;
        break;
      case DESTINATION.NOT_GOING:
        backgroundColor = colors.error25;
        borderColor = colors.error200;
        titleColor = colors.error600;
        break;
      default:
        backgroundColor = colors.gray25;
        borderColor = colors.gray200;
        titleColor = colors.brandBlue500;
        break;
    }

    return { borderColor, titleColor, backgroundColor };
  };

  const getDestinationIcon = (destination: string, color: string): React.ReactNode => {
    switch (destination) {
      case DESTINATION.AIR:
        return <IconAirFreight width={20} height={20} color={color} />;
      case DESTINATION.SEA:
        return <IconSeaFreight width={20} height={20} color={color} />;
      case DESTINATION.ROAD:
        return <IconRoadFreight width={20} height={20} color={color} />;
      case DESTINATION.RESIDENCE:
        return <IconResidence width={20} height={20} color={color} />;
      case DESTINATION.NOT_GOING:
        return <IconNotGoing width={20} height={20} color={color} />;
      default:
        return null;
    }
  };

  const renderSection = (props: { title: string; quantity: number }) => {
    const { title, quantity } = props;

    const { backgroundColor, borderColor, titleColor } = getDestinationCardColors(title);

    if (quantity && quantity > 0) {
      return (
        <InfoCard backgroundColor={backgroundColor} borderColor={borderColor} key={title}>
          <InfoHeader>
            {getDestinationIcon(title, titleColor)}
            <InfoTitle color={titleColor}>{title}</InfoTitle>
          </InfoHeader>

          <InfoContent>
            <InfoTextContent>
              <InfoText>{translate("items")}</InfoText>
              <InfoTextValue>{quantity}</InfoTextValue>
            </InfoTextContent>
          </InfoContent>
        </InfoCard>
      );
    }

    return null;
  };

  const { quantity: quantityNotGoing } = calculateByDestination(
    inventory,
    measurementWeight,
    undefined,
    DESTINATION.NOT_GOING
  );

  return (
    <SummarySection>
      <SummaryHeader>
        <SummaryTitle>{translate("summary")}</SummaryTitle>
        <SummaryText>{translate("perDestination")}</SummaryText>
      </SummaryHeader>
      {Object.keys(addressDestinationItems).map((addressId) => {
        const address = destinationAddresses?.find(
          (addressItem) => String(addressItem.id) === String(addressId?.replace("id-", ""))
        );
        const items = addressDestinationItems[addressId];

        if (!address || !items) return null;

        return renderSection({
          title: translate("residence"),
          ...getItemCounts(items, measurementWeight),
        });
      })}

      {Object.keys(customDestinationItems).map((destinationId) => {
        const destination = destinationList?.find(
          (destination) => String(destination.id) === String(destinationId?.replace("id-", ""))
        );

        const items = customDestinationItems[destinationId];

        if (!destination || !items) return null;

        return renderSection({
          title: destination?.name,
          ...getItemCounts(items, measurementWeight),
        });
      })}

      {Object.keys(customModeDestinationItems).map((modeDestinationId) => {
        const modeDestination = modes?.find(
          (modeDestination) =>
            String(modeDestination.id) === String(modeDestinationId?.replace("id-", ""))
        );
        const items = customModeDestinationItems[modeDestinationId];

        if (!modeDestination || !items) return null;

        return renderSection({
          title: modeDestination.name,
          ...getItemCounts(items, measurementWeight),
        });
      })}

      {renderSection({
        title: DESTINATION.NOT_GOING,
        quantity: quantityNotGoing,
      })}
    </SummarySection>
  );
};
